import { useState } from 'react';
import { ChevronDown, Keyboard } from 'lucide-react';
import { KeyboardModal } from '@/components/keyboard';
import { Textarea } from '@/components/ui/textarea';
import { TaskItemCard } from '@/features/contribution-kit';
import { cn } from '@/lib/utils';
import { fill } from '../copy';
import type { InitialDataCopy } from '../initial-data-copy';
import type { TextAnswer } from '../types';

type TextAnswerPanelProps = {
  copy: InitialDataCopy;
  language: string;
  languageName: string;
  answer: TextAnswer;
  disabled: boolean;
  onChange: (patch: Partial<TextAnswer>) => void;
};

const fieldClass = 'resize-none rounded-lg border-border/60 bg-surface text-base leading-7 placeholder:text-faint-foreground';

type KeyboardTarget = 'response' | 'questionTranslation' | null;

/** The written answer, its character count and the optional translation of the question. */
export function TextAnswerPanel({ copy, language, languageName, answer, disabled, onChange }: TextAnswerPanelProps) {
  const [keyboardTarget, setKeyboardTarget] = useState<KeyboardTarget>(null);
  const [showTranslation, setShowTranslation] = useState(Boolean(answer.questionTranslation));

  const keyboardButton = (target: Exclude<KeyboardTarget, null>) => (
    <button
      type="button"
      onClick={() => setKeyboardTarget(target)}
      disabled={disabled}
      className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-surface px-3 text-xs font-medium text-foreground transition-colors hover:border-brand/40 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-45"
    >
      <Keyboard className="size-3.5" strokeWidth={1.5} />
      {copy.keyboard}
    </button>
  );

  return (
    <TaskItemCard label={copy.answerLabel} corner={keyboardButton('response')}>
      <label htmlFor="initial-data-response" className="block text-base font-medium text-foreground">
        {fill(copy.answerIn, { language: languageName })}
      </label>
      <Textarea
        id="initial-data-response"
        data-testid="initial-data-response"
        value={answer.response}
        disabled={disabled}
        onChange={(event) => onChange({ response: event.target.value })}
        placeholder={fill(copy.textPlaceholder, { language: languageName })}
        className={cn(fieldClass, 'min-h-[150px]')}
      />
      <p className="text-right text-xs tabular-nums text-muted-foreground">
        {fill(copy.charCount, { count: answer.response.trim().length })}
      </p>

      <div className="border-t border-border/60 pt-4">
        <button
          type="button"
          onClick={() => setShowTranslation((open) => !open)}
          aria-expanded={showTranslation}
          className="flex min-h-11 w-full items-center justify-between gap-3 text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {copy.translationToggle}
          <ChevronDown className={cn('size-4 shrink-0 transition-transform', showTranslation && 'rotate-180')} strokeWidth={1.5} />
        </button>
        {showTranslation && (
          <div className="mt-2 space-y-2">
            <div className="flex justify-end">{keyboardButton('questionTranslation')}</div>
            <Textarea
              value={answer.questionTranslation}
              disabled={disabled}
              onChange={(event) => onChange({ questionTranslation: event.target.value })}
              placeholder={fill(copy.translationPlaceholder, { language: languageName })}
              className={cn(fieldClass, 'min-h-[90px]')}
            />
          </div>
        )}
      </div>

      <KeyboardModal
        open={keyboardTarget !== null}
        onOpenChange={(open) => !open && setKeyboardTarget(null)}
        language={language}
        value={keyboardTarget ? answer[keyboardTarget] : ''}
        onChange={(value) => keyboardTarget && onChange({ [keyboardTarget]: value })}
      />
    </TaskItemCard>
  );
}
